import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMessage, faUser, faClock, faChevronRight, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { skipToken } from '@reduxjs/toolkit/query/react';
import {
  useGetAdminCommerceChatConversationsQuery
} from '../../store/admin/adminApi';
import AdminChatModal from '../../components/admin/AdminChatModal';
import { format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { useParams } from 'react-router-dom';

const AdminChatsPage: React.FC = () => {
  const { comercioKey } = useParams<{ comercioKey: string }>();
  const { i18n } = useTranslation();
  const dateLocale = i18n.language.startsWith('es') ? es : enUS;

  const { data: conversations, isLoading, error } = useGetAdminCommerceChatConversationsQuery(comercioKey ?? skipToken);

  const [selectedChatId, setSelectedChatId] = useState<number | null>(null);

  if (!comercioKey) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <FontAwesomeIcon icon={faMessage} className="text-6xl text-white/10 mb-4" />
        <p className="text-white/40">Select a Commerce to view its chats</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-20">
        <FontAwesomeIcon icon={faSpinner} className="text-3xl text-pink-500 animate-spin" />
      </div>
    );
  }

  if (error || !conversations) {
    return (
      <div className="p-8 text-center text-red-400 bg-red-400/10 rounded-2xl border border-red-400/20">
        Failed to load chats for {comercioKey}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 w-full max-w-6xl mx-auto mt-6">
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <FontAwesomeIcon icon={faMessage} className="text-pink-400" />
            Clinic Chats
          </h2>
          <p className="text-white/50 text-sm">
            View patient conversations for {comercioKey}
          </p>
        </div>
        <div className="bg-white/5 border border-white/10 px-4 py-2 rounded-xl text-sm font-medium text-white/80">
          {conversations.length} total threads
        </div>
      </div>

      <div className="bg-black/20 backdrop-blur-md rounded-2xl border border-white/10 overflow-hidden shadow-2xl">
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left text-sm text-white/80">
            <thead className="text-xs uppercase bg-white/5 text-white/60 border-b border-white/10">
              <tr>
                <th className="px-6 py-4 font-semibold">Patient</th>
                <th className="px-6 py-4 font-semibold">Status</th>
                <th className="px-6 py-4 font-semibold">Last Activity</th>
                <th className="px-6 py-4 font-semibold text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {conversations.map((conv) => (
                <tr key={conv.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors group">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500/20 to-purple-500/20 flex items-center justify-center border border-white/10">
                        <FontAwesomeIcon icon={faUser} className="text-indigo-400" />
                      </div>
                      <div className="flex flex-col">
                        <span className="font-semibold text-white group-hover:text-pink-300 transition-colors">
                          {conv.patient_name}
                        </span>
                        <span className="text-xs text-white/40">ID: {conv.patient_id}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {conv.is_saved ? (
                      <span className="px-2 py-1 rounded-md bg-green-500/10 text-green-400 text-xs font-bold border border-green-500/20">Resolved</span>
                    ) : (
                      <span className="px-2 py-1 rounded-md bg-orange-500/10 text-orange-400 text-xs font-bold border border-orange-500/20">Active</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 text-white/50">
                      <FontAwesomeIcon icon={faClock} className="text-xs" />
                      {format(new Date(conv.updated_at), 'PPp', { locale: dateLocale })}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => setSelectedChatId(conv.id)}
                      className="px-4 py-2 bg-white/5 hover:bg-pink-500 hover:text-white text-white/80 rounded-xl transition-all font-medium text-sm border border-white/10 hover:border-pink-500 shadow-lg hover:shadow-[0_0_15px_rgba(236,72,153,0.5)] flex items-center gap-2 ml-auto"
                    >
                      View Thread <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
                    </button>
                  </td>
                </tr>
              ))}
              {conversations.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-white/40">
                    No chat conversations found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedChatId && (
        <AdminChatModal
          comercioKey={comercioKey}
          conversationId={selectedChatId}
          onClose={() => setSelectedChatId(null)}
        />
      )}
    </div>
  );
};

export default AdminChatsPage;
